/**
 * Thin progress bar pinned to the top of the viewport while a request is in
 * flight.
 *
 * WHY INDETERMINATE, AND WHY IT CREEPS
 * ------------------------------------
 * We cannot know how far along a request is — there is no progress event for
 * "the database is still sorting". So the bar advances quickly at first and then
 * slows asymptotically, never reaching 100% until the response actually lands.
 * That shape is honest about what it knows: it says "still working" without ever
 * claiming a completion it cannot see.
 *
 * It is the one element allowed to be fixed-position, because it occupies 3px at
 * the very top and covers nothing. A spinner in the middle of the list would
 * cover content; a skeleton alone leaves the tab looking idle.
 */

import { useEffect, useRef, useState } from 'react';

/** Milliseconds before the bar appears at all. */
const APPEAR_AFTER_MS = 120;

/** How long the finished bar lingers at 100% before fading. */
const FINISH_LINGER_MS = 220;

export function LoadingBar({ active }: { active: boolean }): React.JSX.Element | null {
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const clearAll = (): void => {
      for (const t of timers.current) window.clearTimeout(t);
      timers.current = [];
    };

    if (active) {
      // A request that finishes in under ~120ms should not flash a bar; the
      // flicker reads as a glitch rather than as feedback.
      const appear = window.setTimeout(() => {
        setVisible(true);
        setProgress(8);
      }, APPEAR_AFTER_MS);
      timers.current.push(appear);
      return clearAll;
    }

    // Completing: jump to full, hold briefly so the eye registers it, then hide.
    setProgress((p) => (p > 0 ? 100 : 0));
    const hide = window.setTimeout(() => {
      setVisible(false);
      setProgress(0);
    }, FINISH_LINGER_MS);
    timers.current.push(hide);
    return clearAll;
  }, [active]);

  // Creep toward — but never to — the end while the request is outstanding.
  useEffect(() => {
    if (!active || !visible) return;
    const id = window.setInterval(() => {
      setProgress((p) => {
        if (p >= 92) return p;
        // Each step covers a shrinking fraction of what is left, so the bar
        // decelerates the longer it waits rather than stalling abruptly.
        return p + Math.max(0.4, (92 - p) * 0.08);
      });
    }, 180);
    return () => window.clearInterval(id);
  }, [active, visible]);

  if (!visible) return null;

  return (
    <div
      className="loadbar"
      role="progressbar"
      aria-label="Loading results"
      // Indeterminate to assistive tech: the numeric value is a comfort
      // animation, not a measurement, so it is not announced as one.
      aria-valuetext="Loading"
    >
      <span className="loadbar-fill" style={{ width: `${progress}%` }} />
    </div>
  );
}
